import ClosureFields from '@nile/mangifera/src/Components/Ticket/ClosureFields';
import { isEmpty } from 'lodash';
import React, { useRef, useState } from 'react';
import AICopyAndPaste from '../AICopyAndPaste';
import { SVGIcon } from '@superops/super-icons';
import { TextStyle } from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  EventEmitter,
  getTranslatedText,
  toast,
  useActivityTracker
} from '@nile/components';
import {
  GET_AI_SUGGESTED_PROPERTIES,
  MLClient,
  UPDATE_TICKET
} from '@nile/graphql';
import { useMutation, useQuery } from '@apollo/client';
import RandomText from '@nile/components/src/Components/AI/components/RandomText';
import InlineLoader from './InlineLoader';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore.tsx';

const CustomLabel = ({ label }) => {
  return (
    <div className="mb-5 fsize12">
      <TextStyle variation="aiSubdued" weight="Medium">
        {label}
      </TextStyle>
    </div>
  );
};

const UpdateTicketCard = ({
  data,
  parentTicketId,
  collapeOnInsert,
  onCollapseComplete,
  mixpanelEventOnInsert
}) => {
  const intl = useIntl();
  const aiSuggestedProperties = useAIRecommendedSolutionsStore(
    (s) => s.aiSuggestedProperties
  );
  const setAISuggestedProperties = useAIRecommendedSolutionsStore(
    (s) => s.setAISuggestedProperties
  );

  const closureFields = useAIRecommendedSolutionsStore((s) => s.closureFields);
  const setClosureFields = useAIRecommendedSolutionsStore(
    (s) => s.setClosureFields
  );

  const [collapsed, setCollapsed] = useState(false);

  const ticketPropertyLoadingText = [
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.loading.message.1'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.loading.message.2'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.loading.message.3'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.loading.message.4'
    )
  ];

  const emptyTicketPropertiesMessages = [
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.empty.message.1'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.empty.message.2'
    ),
    getTranslatedText(
      intl,
      'ai.recommended.solutions.ticket.properties.empty.message.3'
    )
  ];

  const ticketVariablesRef = useRef({
    id: parentTicketId
  });

  const getSuggestedProperyVariables = () => {
    const recommendedTickets = data?.tickets || [];
    const recommendedSolutionTicketIds = recommendedTickets.map(
      (ticket) => ticket?.ticketId
    );

    const variables = {
      ticketId: parentTicketId,
      recommendedSolutionTicketIds: recommendedSolutionTicketIds
    };

    return variables;
  };

  const suggestedPropertyVariables = getSuggestedProperyVariables();
  const {
    loading: suggestedPropertiesLoading,
    data: suggestedPropertiesData
  } = useQuery(GET_AI_SUGGESTED_PROPERTIES, {
    fetchPolicy: 'no-cache',
    variables: suggestedPropertyVariables,
    client: MLClient,
    skip: !aiSuggestedProperties,
    onCompleted: (res) => {
      if (res?.getAISuggestedProperties) {
        const suggestedProps = res?.getAISuggestedProperties;
        setAISuggestedProperties(suggestedProps);
        if (!isEmpty(suggestedProps)) {
          const defaultProperties = suggestedProps?.defaultProperties || [];
          const customProperties = suggestedProps?.additionalProperties || [];
          const allProperties = []
            .concat(defaultProperties)
            .concat(customProperties);
          let fieldsData = {};
          allProperties.forEach((field) => {
            const fieldName = field?.name;
            const fieldValue = field?.value;
            if (fieldName && fieldValue) {
              const isCustomField = customProperties.find(
                (prop) => prop?.name === fieldName
              );
              fieldsData = {
                ...fieldsData,
                [fieldName]: fieldValue
              };
              if (isCustomField) {
                let customFieldVariables =
                  ticketVariablesRef?.current?.customFields || {};
                customFieldVariables = {
                  ...customFieldVariables,
                  [fieldName]: fieldValue
                };
                ticketVariablesRef.current = {
                  ...ticketVariablesRef?.current,
                  customFields: customFieldVariables
                };
              } else {
                ticketVariablesRef.current = {
                  ...ticketVariablesRef?.current,
                  [fieldName]: fieldValue
                };
              }
            }
          });
          setClosureFields(fieldsData);
        }
      }
    }
  });

  const onClosureFieldsChange = (field, value) => {
    const fieldName = field?.columnName;
    const isCustomField = field?.fieldCategory === 'CUSTOM';
    const updatedValue = value?.value;
    if (fieldName && updatedValue) {
      if (isCustomField) {
        let customFieldVariables =
          ticketVariablesRef?.current?.customFields || {};
        customFieldVariables = {
          ...customFieldVariables,
          [fieldName]: updatedValue
        };
        ticketVariablesRef.current = {
          ...ticketVariablesRef?.current,
          customFields: customFieldVariables
        };
      } else {
        ticketVariablesRef.current = {
          ...ticketVariablesRef?.current,
          [fieldName]: updatedValue
        };
      }
    }
  };

  const [updateTicket] = useMutation(UPDATE_TICKET, {
    onCompleted(res) {
      /* Close sheet container once an employee created */
      if (res?.updateTicket) {
        if (mixpanelEventOnInsert) {
          useActivityTracker(mixpanelEventOnInsert);
        }
        EventEmitter.dispatch('refetchTicketFromAI');
        toast('success', 'app.toast.ticket.update.success');
        if (collapeOnInsert) {
          setTimeout(() => {
            // Added time out for insterted animation
            setCollapsed(true);
          }, 500);
          if (onCollapseComplete) {
            setTimeout(() => {
              // Adding timeout for shrink
              onCollapseComplete();
            }, 2000);
          }
        }
      }
    }
  });

  const updateTicketWithVariables = () => {
    updateTicket({
      variables: ticketVariablesRef?.current
    });
  };

  if (suggestedPropertiesLoading) {
    return (
      <InlineLoader variation="aiSubdued" text={ticketPropertyLoadingText} />
    );
  }

  if (!suggestedPropertiesLoading && isEmpty(closureFields)) {
    return (
      <div className="tcenter mt5 mb5">
        <RandomText
          variation="aiSubdued"
          text={emptyTicketPropertiesMessages}
        />
      </div>
    );
  }

  const classNames = [
    'p10 br10 ai-dark-bg ai-update-ticket-wrapper ai-collapsed-card',
    collapsed && 'collapsed'
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} id="update-ai-ticket-card">
      <div className="flex actr fx-sb mb10">
        <div className="flex g10 actr ellipsis">
          <div className="flex">
            <SVGIcon name="AI_TICKET" />
          </div>
          <div className="ellipsis">
            <TextStyle weight="Medium">
              <FormattedMessage id="ai.suggested.ticket.property" />
            </TextStyle>
          </div>
        </div>

        <div>
          <AICopyAndPaste
            data={{
              copyButtonText: getTranslatedText(intl, 'common.insert'),
              copySuccessText: getTranslatedText(intl, 'common.inserted'),
              onCopy: updateTicketWithVariables
            }}
          />
        </div>
      </div>

      {!isEmpty(closureFields) && (
        <ClosureFields
          fields={closureFields}
          onChange={onClosureFieldsChange}
          noPortalTarget
          isBulkSection
          customWidthClass="w46p"
          customLabelComponent={CustomLabel}
          isUiDesignSystem
        />
      )}
    </div>
  );
};

export default UpdateTicketCard;
